import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Save, Loader2, User, Mail, Shield } from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfileSettings() {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [fullName, setFullName] = useState(user?.full_name || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleSave = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) {
      toast.error("Name is required");
      return;
    }
    if (password && password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password && password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      const payload = { full_name: fullName.trim() };
      if (password) payload.password = password;
      await axios.put(`${API}/profile`, payload, { withCredentials: true });
      toast.success("Profile updated");
      setPassword("");
      setConfirmPassword("");
      if (refreshUser) refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl space-y-6" data-testid="profile-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="profile-title">
            Profile Settings
          </h2>
          <p className="text-[#6E7787] font-body text-sm mt-1">
            Update your name and password
          </p>
        </div>
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving || !fullName.trim()}
          data-testid="profile-save-btn"
          className="h-9 px-5 font-body font-medium text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
        >
          {saving ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-1.5" />Saving...</>
          ) : (
            <><Save className="h-4 w-4 mr-1.5" />Save Changes</>
          )}
        </Button>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-6 space-y-5">
          {/* Avatar + Role */}
          <div className="flex items-center gap-4 pb-4 border-b border-white/5">
            <div
              className="h-14 w-14 rounded-full flex items-center justify-center text-lg font-bold text-white shrink-0"
              style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
            >
              {fullName?.[0]?.toUpperCase() || "A"}
            </div>
            <div>
              <p className="text-base font-body font-bold text-white">{fullName || "Admin"}</p>
              <Badge
                data-testid="profile-role-badge"
                className={
                  user?.role === "system_admin"
                    ? "bg-violet-500/15 text-violet-400 border-violet-500/20 mt-1"
                    : user?.role === "instructor"
                      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20 mt-1"
                      : "bg-sky-500/15 text-sky-400 border-sky-500/20 mt-1"
                }
              >
                {user?.role === "system_admin" ? "System Admin" : user?.role === "instructor" ? "Instructor" : "Admin"}
              </Badge>
            </div>
          </div>

          {/* Full Name */}
          <div className="space-y-1.5">
            <Label className="text-[#A1A9B8] font-body text-sm">Full Name <span className="text-red-400">*</span></Label>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              data-testid="profile-name-input"
              className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10"
            />
          </div>

          {/* Email (read-only) */}
          <div className="space-y-1.5">
            <Label className="text-[#A1A9B8] font-body text-sm flex items-center gap-1.5">
              <Mail className="h-3 w-3" /> Email
            </Label>
            <Input
              value={user?.email || ""}
              disabled
              data-testid="profile-email-input"
              className="bg-[#0A0C10] border-white/10 text-[#6E7787] h-10 cursor-not-allowed"
            />
            <p className="text-xs text-[#6E7787] font-body">Email cannot be changed</p>
          </div>
        </div>

        {/* Password Section */}
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-6 space-y-5">
          <h3 className="font-body text-base font-bold text-white flex items-center gap-2">
            <Shield className="h-4 w-4" style={{ color: "var(--primary-color, #4A90D9)" }} />
            Change Password
          </h3>
          <p className="text-xs text-[#6E7787] font-body -mt-3">Leave blank to keep your current password</p>

          <div className="space-y-1.5">
            <Label className="text-[#A1A9B8] font-body text-sm">New Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min 6 characters"
              data-testid="profile-password-input"
              className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] h-10"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#A1A9B8] font-body text-sm">Confirm Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              data-testid="profile-confirm-password-input"
              className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] h-10"
            />
          </div>
        </div>
      </form>
    </div>
  );
}
